import { getApiClient } from '@src/shared/types/api-client'
import { investigationKeys } from './query-keys'
import {
  InvestigationSearchRequest,
  InvestigationSearchResult,
  TracesResult,
  OrchestrationDto,
} from '@src/tools/investigation/types/Constants'
import { MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

const searchCreditApp: MutationFunction<InvestigationSearchResult, InvestigationSearchRequest> = async (request) => {
  const apiClient = getApiClient()
  const response = await apiClient.post('investigation/search', request)
  return response.data as InvestigationSearchResult
}

export function useInvestigationSearch(): [
  MutationFunction<InvestigationSearchResult, InvestigationSearchRequest>,
  boolean,
  boolean,
  () => void,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, isError, reset } = useMutation({
    mutationFn: searchCreditApp,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: investigationKeys.all() })
    },
  })

  return [mutateAsync, isPending, isError, reset]
}

const getTraces = async (creditApplicationId: string, days = 7): Promise<TracesResult> => {
  const apiClient = getApiClient()
  const response = await apiClient.get(`investigation/${encodeURIComponent(creditApplicationId)}/traces?days=${days}`)
  return response.data as TracesResult
}

export function useInvestigationTraces(creditApplicationId: string | null): [TracesResult | undefined, boolean] {
  const { data, isFetching } = useQuery({
    queryKey: investigationKeys.traces(creditApplicationId ?? ''),
    queryFn: () => getTraces(creditApplicationId!),
    enabled: !!creditApplicationId,
  })

  return [data, isFetching]
}

const getOrchestrations = async (creditApplicationId: string, instanceIds: string[]): Promise<OrchestrationDto[]> => {
  const apiClient = getApiClient()
  const response = await apiClient.get(
    `investigation/${encodeURIComponent(creditApplicationId)}/orchestrations?instanceIds=${instanceIds.join(',')}`
  )
  return response.data as OrchestrationDto[]
}

export function useInvestigationOrchestrations(
  creditApplicationId: string | null,
  instanceIds: string[]
): [OrchestrationDto[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: investigationKeys.orchestrations(creditApplicationId ?? '', instanceIds),
    queryFn: () => getOrchestrations(creditApplicationId!, instanceIds),
    enabled: !!creditApplicationId && instanceIds.length > 0,
    placeholderData: [],
  })

  return [data ?? [], isFetching]
}
